import type { ReactNode } from 'react';

import { Button, ButtonText } from './ui/button';

import { RiveAnimation } from '@/components/RiveAnimation';
import { Heading } from '@/components/heading';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';

export type EmptyQueryResultsProps = {
  title?: string;
  text?: string;
  cta?: string;
  onCtaPress: () => void;
  /** Extra content under the button, for a way out that only some screens have. */
  children?: ReactNode;
};

/**
 * The block takes the space left under whatever list or screen holds it and centers its
 * content there, and it is never taller than its content needs. A viewport-relative height
 * was tried and put the heading and button a full screen below the list header on a phone,
 * leaving only the top of the animation in view, which read as a spinner that never finished.
 */
export function EmptyQueryResults({
  title = 'No results',
  text = 'Check your spelling, or try a different search',
  cta = 'Clear filters',
  onCtaPress,
  children,
}: EmptyQueryResultsProps) {
  return (
    <VStack
      testID="empty-query-results"
      className="m-0 grow items-center justify-center px-4 py-8 web:m-0 web:h-auto web:min-h-[60vh] web:grow-0 web:py-16">
      <VStack className="mb-6 h-[128px] w-[128px]">
        <RiveAnimation name="ACCESSING_NETWORK" />
      </VStack>

      <Heading size="h2-lg" className="mb-2">
        {title}
      </Heading>

      <Text className="mb-6 text-center">{text}</Text>

      <Button variant="outline" onPress={onCtaPress}>
        <ButtonText>{cta}</ButtonText>
      </Button>

      {children ? <VStack className="mt-6 items-center">{children}</VStack> : null}
    </VStack>
  );
}
